// pages/StaffPanel.js - Fixed to show only assigned orders
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrders } from '../hooks/useOrders';
import { dbOnValue } from '../config/firebase';
import { 
  Menu, X, Package, XCircle, LogOut, 
  Clock, CheckCircle, Truck, Phone, 
  Calendar, DollarSign, Eye, ChevronRight, Home,
  RefreshCw, Search, Filter, User,
  TrendingUp, ChevronDown,
  CreditCard, Wallet, 
  User as UserIcon, Mail, Phone as PhoneIcon,
  ClipboardList, ChevronLeft, MapPin
} from 'lucide-react';
import { toast } from 'react-toastify';

const StaffPanel = () => {
  const { user, isStaff, isAdmin, logout } = useAuth();
  const { updateOrderStatus, refreshKey, processOrder, dispatchOrder, verifyDeliveryOTP } = useOrders();
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('current');
  const [myAssignedOrders, setMyAssignedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [deliveryOTP, setDeliveryOTP] = useState('');
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedHour, setSelectedHour] = useState('10');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const [selectedPeriod, setSelectedPeriod] = useState('AM');
  const navigate = useNavigate();

  const hours = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
  const periods = ['AM', 'PM'];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load all orders from Firebase
  useEffect(() => {
    const unsubscribe = dbOnValue('orders', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const ordersList = Object.entries(data).map(([key, order]) => ({
          firebaseKey: key,
          id: order.id || key,
          ...order
        }));
        setOrders(ordersList.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate)));
      } else {
        setOrders([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [refreshKey]);

  // Get current staff member's assigned orders - IMPROVED FILTERING
  // Get current staff member's assigned orders - FIXED
useEffect(() => {
  if (user && orders.length > 0) {
    // Get the current logged-in staff member's ID from Firebase key
    const currentStaffId = user.firebaseKey || user.$id || user.uid || user.id;
    const currentStaffName = user.name;
    const currentStaffEmail = user.email;
    
    console.log('Current Staff ID:', currentStaffId);
    console.log('Current Staff Name:', currentStaffName);
    
    const assignedOrders = orders.filter(order => {
      // STRICT MATCH - Only show orders where assignedToId EXACTLY matches current staff
      const isAssignedToMe = order.assignedToId === currentStaffId;
      
      // Also check by name as fallback
      const isAssignedByName = order.assignedTo === currentStaffName;
      
      if (isAssignedToMe || isAssignedByName) {
        console.log('Order assigned to me:', order.id);
        return true;
      }
      return false;
    });
    
    console.log('Total assigned orders:', assignedOrders.length);
    setMyAssignedOrders(assignedOrders);
  }
}, [user, orders]);
  // Categorize orders
  const currentOrders = myAssignedOrders.filter(o => ['confirmed', 'processing'].includes(o.orderStatus));
  const deliveryOrders = myAssignedOrders.filter(o => o.orderStatus === 'out');
  const completedOrders = myAssignedOrders.filter(o => o.orderStatus === 'delivered');

  const getFilteredOrders = () => {
    let filtered = myAssignedOrders;
    
    if (activeTab === 'current') {
      filtered = currentOrders;
    } else if (activeTab === 'delivery') {
      filtered = deliveryOrders;
    } else if (activeTab === 'completed') {
      filtered = completedOrders;
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order => 
        order.id?.toLowerCase().includes(term) ||
        order.customerName?.toLowerCase().includes(term) ||
        order.customerPhone?.includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.orderStatus === statusFilter);
    }
    
    return filtered;
  };

  const displayOrders = getFilteredOrders();
  const stats = {
    total: myAssignedOrders.length,
    current: currentOrders.length,
    delivery: deliveryOrders.length,
    completed: completedOrders.length,
    totalRevenue: myAssignedOrders.filter(o => o.orderStatus === 'delivered').reduce((sum, o) => sum + (o.totalAmount || 0), 0),
    pendingRevenue: myAssignedOrders.filter(o => o.orderStatus === 'out').reduce((sum, o) => sum + (o.totalAmount || 0), 0),
    completionRate: myAssignedOrders.length ? ((completedOrders.length / myAssignedOrders.length) * 100).toFixed(1) : 0
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fef3c7', color: '#d97706', label: 'Pending', icon: Clock },
      confirmed: { bg: '#dbeafe', color: '#2563eb', label: 'Confirmed', icon: CheckCircle },
      processing: { bg: '#fef3c7', color: '#d97706', label: 'Processing', icon: Package },
      out: { bg: '#ede9fe', color: '#7c3aed', label: 'Out for Delivery', icon: Truck },
      delivered: { bg: '#d1fae5', color: '#059669', label: 'Delivered', icon: CheckCircle },
      cancelled: { bg: '#fee2e2', color: '#dc2626', label: 'Cancelled', icon: XCircle }
    };
    return styles[status] || { bg: '#f1f5f9', color: '#64748b', label: status, icon: Package };
  };

  // Order action handlers
  const handleProcessOrder = async (order) => {
    const identifier = order.firebaseKey || order.id;
    const result = await processOrder(identifier);
    if (result.success) {
      toast.success('Order is now being processed');
      setTimeout(() => window.location.reload(), 1000);
    } else {
      toast.error(result.message);
    }
  };

  const handleOpenTimeModal = (order) => {
    setSelectedOrder(order);
    setShowTimeModal(true);
  };

  const handleDispatch = async () => {
    if (!selectedOrder) return;
    const timeString = `${selectedHour}:${selectedMinute} ${selectedPeriod}`;
    const identifier = selectedOrder.firebaseKey || selectedOrder.id;
    const result = await dispatchOrder(identifier, timeString);
    if (result.success) {
      toast.success(`Order dispatched for delivery`);
      setShowTimeModal(false);
      setSelectedOrder(null);
      setTimeout(() => window.location.reload(), 1000);
    } else {
      toast.error(result.message);
    }
  };

  const handleOpenOTPModal = (order) => {
    setSelectedOrder(order);
    setDeliveryOTP('');
    setShowOTPModal(true);
  };

  const handleVerifyDelivery = async () => {
    if (!deliveryOTP || deliveryOTP.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    const identifier = selectedOrder.firebaseKey || selectedOrder.id;
    const result = await verifyDeliveryOTP(identifier, deliveryOTP);
    if (result === true || result?.success) {
      toast.success('Order delivered successfully!');
      setShowOTPModal(false);
      setDeliveryOTP('');
      setSelectedOrder(null);
      setTimeout(() => window.location.reload(), 1000);
    } else {
      toast.error(result?.message || 'Invalid OTP. Please try again.');
    }
  };

  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'orders', label: 'My Orders', icon: Package, count: myAssignedOrders.length }
  ];

  if (!isStaff && !isAdmin) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ background: 'white', borderRadius: '20px', padding: '30px', textAlign: 'center', maxWidth: '350px', width: '100%' }}>
          <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <span style={{ fontSize: '2rem' }}>🔒</span>
          </div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '8px' }}>Access Denied</h2>
          <p style={{ color: '#64748b', marginBottom: '20px', fontSize: '0.85rem' }}>You don't have permission to access the staff panel.</p>
          <button onClick={() => navigate('/')} style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', width: '100%' }}>Go to Website</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f8fafc' }}>
        <div className="spinner" style={{ width: 40, height: 40, border: '3px solid #dc2626', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', display: 'flex' }}>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        style={{
          position: 'fixed',
          top: '12px',
          left: '12px',
          zIndex: 1001,
          background: '#dc2626',
          border: 'none',
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          display: isMobile ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(220,38,38,0.3)',
          transition: 'all 0.2s'
        }}
      >
        {mobileMenuOpen ? <X size={18} color="white" /> : <Menu size={18} color="white" />}
      </button>

      {/* Sidebar - Mobile Optimized */}
      <aside style={{
        width: '260px',
        background: '#1a1a2e',
        color: '#e2e8f0',
        height: '100vh',
        position: 'fixed',
        left: isMobile && !mobileMenuOpen ? '-260px' : '0',
        top: 0,
        transition: 'left 0.3s ease',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '2px 0 10px rgba(0,0,0,0.1)'
      }}>
        <div style={{ padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              background: 'linear-gradient(135deg, #dc2626, #b91c1c)', 
              borderRadius: '12px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              fontWeight: 700, 
              fontSize: '1.2rem', 
              color: 'white'
            }}>
              S
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: 'white' }}>Staff Portal</div>
              <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '2px' }}>{user?.name || 'Staff Member'}</div>
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, padding: '16px 12px' }}>
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setCurrentPage(item.id); setMobileMenuOpen(false); }}
              style={{
                width: '100%',
                padding: '12px 14px',
                marginBottom: '6px',
                borderRadius: '10px',
                border: 'none',
                background: currentPage === item.id ? 'rgba(220,38,38,0.15)' : 'transparent',
                color: currentPage === item.id ? '#dc2626' : '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '0.85rem',
                fontWeight: currentPage === item.id ? 600 : 400
              }}
            >
              <item.icon size={18} />
              <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
              {item.count > 0 && (
                <span style={{
                  background: currentPage === item.id ? '#dc2626' : '#334155',
                  padding: '2px 6px',
                  borderRadius: '20px',
                  fontSize: '0.65rem',
                  color: 'white'
                }}>{item.count}</span>
              )}
            </button>
          ))}
        </nav>

        <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, color: 'white', fontSize: '0.9rem' }}>
              {user?.name?.[0]?.toUpperCase() || 'S'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'white' }}>{user?.name || 'Staff'}</div>
              <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{user?.email?.split('@')[0]}</div>
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate('/'); }}
            style={{
              width: '100%',
              padding: '10px',
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: '8px',
              color: '#f87171',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobile && mobileMenuOpen && (
        <div onClick={() => setMobileMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, animation: 'fadeIn 0.2s ease' }} />
      )}

      {/* Main Content - Mobile Optimized */}
      <main style={{
        flex: 1,
        marginLeft: isMobile ? '0' : '260px',
        padding: isMobile ? '70px 12px 24px' : '32px 32px',
        width: isMobile ? '100%' : 'calc(100% - 260px)',
        minHeight: '100vh'
      }}>
        {currentPage === 'dashboard' ? (
          <div>
            {/* Welcome Section - Mobile Optimized */}
            <div style={{ marginBottom: '20px' }}>
              <h1 style={{ fontSize: isMobile ? '1.3rem' : '1.8rem', fontWeight: 700, color: '#1a0a0a', marginBottom: '4px' }}>
                Welcome back, {user?.name?.split(' ')[0] || 'Staff'}! 👋
              </h1>
              <p style={{ color: '#64748b', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>Here's what's happening with your deliveries today.</p>
            </div>

            {/* Stats Cards - Mobile Optimized Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
              gap: isMobile ? '10px' : '20px',
              marginBottom: '20px'
            }}>
              <div style={{ 
                background: 'white', 
                borderRadius: '16px', 
                padding: isMobile ? '12px' : '20px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                cursor: 'pointer'
              }}
              onClick={() => { setCurrentPage('orders'); setActiveTab('current'); }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ width: isMobile ? '36px' : '48px', height: isMobile ? '36px' : '48px', background: '#fef3c7', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ClipboardList size={isMobile ? 18 : 24} color="#f59e0b" />
                  </div>
                  <span style={{ fontSize: '0.6rem', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '12px' }}>Current</span>
                </div>
                <div style={{ fontSize: isMobile ? '1.5rem' : '1.8rem', fontWeight: 700, color: '#1a0a0a' }}>{stats.current}</div>
                <div style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', color: '#64748b' }}>In Progress</div>
              </div>

              <div style={{ 
                background: 'white', 
                borderRadius: '16px', 
                padding: isMobile ? '12px' : '20px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                cursor: 'pointer'
              }}
              onClick={() => { setCurrentPage('orders'); setActiveTab('delivery'); }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ width: isMobile ? '36px' : '48px', height: isMobile ? '36px' : '48px', background: '#ede9fe', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Truck size={isMobile ? 18 : 24} color="#8b5cf6" />
                  </div>
                  <span style={{ fontSize: '0.6rem', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '12px' }}>Delivery</span>
                </div>
                <div style={{ fontSize: isMobile ? '1.5rem' : '1.8rem', fontWeight: 700, color: '#1a0a0a' }}>{stats.delivery}</div>
                <div style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', color: '#64748b' }}>Out for Delivery</div>
              </div>

              <div style={{ 
                background: 'white', 
                borderRadius: '16px', 
                padding: isMobile ? '12px' : '20px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                cursor: 'pointer'
              }}
              onClick={() => { setCurrentPage('orders'); setActiveTab('completed'); }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ width: isMobile ? '36px' : '48px', height: isMobile ? '36px' : '48px', background: '#d1fae5', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircle size={isMobile ? 18 : 24} color="#10b981" />
                  </div>
                  <span style={{ fontSize: '0.6rem', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '12px' }}>Completed</span>
                </div>
                <div style={{ fontSize: isMobile ? '1.5rem' : '1.8rem', fontWeight: 700, color: '#1a0a0a' }}>{stats.completed}</div>
                <div style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', color: '#64748b' }}>Delivered</div>
              </div>

           
             
            </div>

            {/* Recent Orders Section - ONLY SHOWS ASSIGNED ORDERS */}
            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
              <div style={{ padding: isMobile ? '12px 16px' : '16px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <h3 style={{ fontSize: isMobile ? '0.9rem' : '1rem', fontWeight: 700, margin: 0 }}>Recent Orders</h3>
                  <p style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '2px' }}>Latest 5 orders assigned to you</p>
                </div>
                {myAssignedOrders.length > 0 && (
                  <button onClick={() => setCurrentPage('orders')} style={{ 
                    color: '#dc2626', 
                    background: 'none', 
                    border: 'none', 
                    cursor: 'pointer', 
                    fontSize: '0.75rem', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px',
                    padding: '6px 12px',
                    borderRadius: '6px'
                  }}>
                    View All ({myAssignedOrders.length}) <ChevronRight size={12} />
                  </button>
                )}
              </div>
              
              {myAssignedOrders.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <div style={{ width: '60px', height: '60px', background: '#fef2f2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <Package size={28} color="#dc2626" />
                  </div>
                  <p style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1a0a0a', marginBottom: '4px' }}>No orders assigned yet</p>
                  <p style={{ fontSize: '0.7rem', color: '#64748b' }}>Orders assigned to you will appear here</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '550px' : '600px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                        <th style={{ textAlign: 'left', padding: isMobile ? '10px 12px' : '12px 16px', fontSize: '0.65rem', fontWeight: 600, color: '#dc2626' }}>Order ID</th>
                        <th style={{ textAlign: 'left', padding: isMobile ? '10px 12px' : '12px 16px', fontSize: '0.65rem', fontWeight: 600, color: '#dc2626' }}>Customer</th>
                        <th style={{ textAlign: 'left', padding: isMobile ? '10px 12px' : '12px 16px', fontSize: '0.65rem', fontWeight: 600, color: '#dc2626' }}>Status</th>
                        <th style={{ textAlign: 'left', padding: isMobile ? '10px 12px' : '12px 16px', fontSize: '0.65rem', fontWeight: 600, color: '#dc2626' }}>Delivery Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myAssignedOrders.slice(0, 5).map((order, idx) => {
                        const badge = getStatusBadge(order.orderStatus);
                        const Icon = badge.icon;
                        return (
                          <tr key={order.id} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            onClick={() => { setSelectedOrder(order); setShowOrderModal(true); }}>
                            <td style={{ padding: isMobile ? '10px 12px' : '12px 16px', fontSize: '0.75rem' }}>
                              <span style={{ fontWeight: 600, fontFamily: 'monospace', background: '#f5f5f5', padding: '4px 6px', borderRadius: '4px', fontSize: '0.7rem' }}>
                                #{order.id?.slice(0, 8)}
                              </span>
                            </td>
                            <td style={{ padding: isMobile ? '10px 12px' : '12px 16px', fontSize: '0.8rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <UserIcon size={12} color="#64748b" />
                                <span style={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>{order.customerName?.split(' ')[0]}</span>
                              </div>
                            </td>
                            <td style={{ padding: isMobile ? '10px 12px' : '12px 16px', fontSize: '0.8rem' }}>
                              <span style={{ 
                                padding: '3px 8px', 
                                borderRadius: '12px', 
                                fontSize: '0.6rem', 
                                fontWeight: 600, 
                                background: badge.bg, 
                                color: badge.color, 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '4px' 
                              }}>
                                <Icon size={10} /> {badge.label === 'Out for Delivery' ? 'Out' : badge.label}
                              </span>
                            </td>
                            <td style={{ padding: isMobile ? '10px 12px' : '12px 16px', fontSize: '0.8rem' }}>
                              {order.deliveryTime ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Clock size={12} color="#f59e0b" />
                                  <span style={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>{order.deliveryTime}</span>
                                </div>
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>—</span>
                              )}
                            </td>
                           </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : (
          // My Orders View - Mobile Optimized
          <div>
            {/* Header */}
            <div style={{ marginBottom: '20px' }}>
              <h1 style={{ fontSize: isMobile ? '1.3rem' : '1.5rem', fontWeight: 700, color: '#1a0a0a', marginBottom: '4px' }}>
                My Orders
              </h1>
              <p style={{ color: '#64748b', fontSize: isMobile ? '0.7rem' : '0.8rem' }}>
                You have <strong style={{ color: '#dc2626' }}>{myAssignedOrders.length}</strong> assigned orders
              </p>
            </div>

            {/* Modern Tabs - Mobile Optimized */}
            <div style={{ 
              display: 'flex', 
              gap: '6px', 
              marginBottom: '16px',
              background: 'white',
              padding: '4px',
              borderRadius: '12px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
            }}>
              {[
                { id: 'current', label: 'Current', count: currentOrders.length, icon: ClipboardList, color: '#f59e0b', bg: '#fef3c7' },
                { id: 'delivery', label: 'Delivery', count: deliveryOrders.length, icon: Truck, color: '#8b5cf6', bg: '#ede9fe' },
                { id: 'completed', label: 'Completed', count: completedOrders.length, icon: CheckCircle, color: '#10b981', bg: '#d1fae5' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flex: 1,
                    padding: isMobile ? '8px 6px' : '10px 12px',
                    borderRadius: '10px',
                    border: 'none',
                    background: activeTab === tab.id ? tab.bg : 'transparent',
                    color: activeTab === tab.id ? tab.color : '#64748b',
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    fontWeight: activeTab === tab.id ? 600 : 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <tab.icon size={isMobile ? 14 : 16} />
                  <span>{tab.label}</span>
                  <span style={{
                    background: activeTab === tab.id ? tab.color : '#e2e8f0',
                    color: activeTab === tab.id ? 'white' : '#64748b',
                    padding: '1px 5px',
                    borderRadius: '12px',
                    fontSize: '0.6rem'
                  }}>{tab.count}</span>
                </button>
              ))}
            </div>

            {/* Search and Filter Bar - Mobile Optimized */}
            <div style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: isMobile ? '10px 12px' : '12px 16px', 
              marginBottom: '16px', 
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
            }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: isMobile ? '150px' : '200px' }}>
                  <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search orders..."
                    style={{ 
                      width: '100%', 
                      padding: isMobile ? '8px 10px 8px 32px' : '10px 12px 10px 36px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '10px', 
                      fontSize: isMobile ? '0.8rem' : '0.85rem', 
                      outline: 'none',
                      background: '#f8fafc'
                    }}
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ 
                    padding: isMobile ? '6px 10px' : '8px 12px', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '10px', 
                    fontSize: isMobile ? '0.75rem' : '0.8rem', 
                    background: 'white',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="processing">Processing</option>
                  <option value="out">Out</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>
            </div>

            {/* Orders List - Mobile Optimized Cards */}
            {displayOrders.length === 0 ? (
              <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center' }}>
                <div style={{ width: '60px', height: '60px', background: '#fef2f2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <Package size={28} color="#dc2626" />
                </div>
                <p style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1a0a0a', marginBottom: '4px' }}>No orders found</p>
                <p style={{ fontSize: '0.7rem', color: '#64748b' }}>Try adjusting your search or filter</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {displayOrders.map(order => {
                  const badge = getStatusBadge(order.orderStatus);
                  const Icon = badge.icon;
                  const isDelivery = order.orderStatus === 'out';
                  const isProcessing = order.orderStatus === 'processing';
                  const isConfirmed = order.orderStatus === 'confirmed';
                  
                  return (
                    <div 
                      key={order.id} 
                      style={{ 
                        background: 'white', 
                        borderRadius: '16px', 
                        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                        border: `1px solid ${isDelivery ? '#dc2626' : '#e2e8f0'}`,
                      }}
                    >
                      {/* Order Header */}
                      <div style={{ 
                        padding: isMobile ? '12px' : '14px', 
                        background: isDelivery ? '#fff5f5' : '#fafafa', 
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ 
                              fontWeight: 600, 
                              fontFamily: 'monospace', 
                              fontSize: '0.75rem', 
                              background: '#f5f5f5', 
                              padding: '3px 8px', 
                              borderRadius: '6px' 
                            }}>
                              #{order.id?.slice(0, 8)}
                            </span>
                            <span style={{ 
                              padding: '3px 8px', 
                              borderRadius: '12px', 
                              fontSize: '0.6rem', 
                              fontWeight: 600, 
                              background: badge.bg, 
                              color: badge.color, 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '4px' 
                            }}>
                              <Icon size={10} /> {badge.label === 'Out for Delivery' ? 'Out' : badge.label}
                            </span>
                          </div>
                          <div style={{ fontWeight: 700, fontSize: isMobile ? '1rem' : '1.1rem', color: '#dc2626' }}>₹{order.totalAmount?.toLocaleString()}</div>
                        </div>
                        {isDelivery && order.deliveryTime && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.65rem', color: '#f59e0b' }}>
                            <Clock size={12} /> Est: {order.deliveryTime}
                          </div>
                        )}
                      </div>
                      
                      {/* Order Body */}
                      <div style={{ padding: isMobile ? '12px' : '16px' }}>
                        {/* Customer Info */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '0.6rem', fontWeight: 600, color: '#dc2626', marginBottom: '6px' }}>Customer</div>
                          <div style={{ fontWeight: 500, fontSize: '0.8rem' }}>{order.customerName}</div>
                          <div style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                            {order.customerPhone}
                            <button 
                              onClick={() => handleCall(order.customerPhone)} 
                              style={{ padding: '4px 10px', background: '#10b981', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                            >
                              <Phone size={12} color="white" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Delivery Address */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '0.6rem', fontWeight: 600, color: '#dc2626', marginBottom: '6px' }}>Delivery Address</div>
                          <div style={{ fontSize: '0.75rem' }}>{order.deliveryAddress?.line1}</div>
                          <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{order.deliveryAddress?.city} - {order.deliveryAddress?.pincode}</div>
                        </div>
                        
                        {/* Order Items Preview - Mobile Optimized */}
                        {order.items && order.items.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '0.6rem', fontWeight: 600, color: '#dc2626', marginBottom: '6px' }}>Items</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {order.items.slice(0, isMobile ? 2 : 3).map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', padding: '4px 8px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                  <img src={item.image} alt={item.name} style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover' }} />
                                  <span style={{ fontSize: '0.65rem' }}>{item.name} x{item.quantity}</span>
                                </div>
                              ))}
                              {order.items.length > (isMobile ? 2 : 3) && (
                                <span style={{ fontSize: '0.65rem', color: '#64748b', padding: '4px 8px' }}>
                                  +{order.items.length - (isMobile ? 2 : 3)} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
                          <button
                            onClick={() => { setSelectedOrder(order); setShowOrderModal(true); }}
                            style={{ 
                              flex: 1,
                              padding: isMobile ? '8px' : '8px 12px', 
                              background: '#f1f5f9', 
                              color: '#475569', 
                              border: 'none', 
                              borderRadius: '8px', 
                              fontSize: isMobile ? '0.7rem' : '0.75rem', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            <Eye size={14} /> View
                          </button>
                          
                          {isConfirmed && (
                            <button
                              onClick={() => handleProcessOrder(order)}
                              style={{ 
                                flex: 1,
                                padding: isMobile ? '8px' : '8px 12px', 
                                background: '#f59e0b', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '8px', 
                                fontSize: isMobile ? '0.7rem' : '0.75rem', 
                                cursor: 'pointer', 
                                fontWeight: 600
                              }}
                            >
                              Process
                            </button>
                          )}
                          
                          {isProcessing && (
                            <button
                              onClick={() => handleOpenTimeModal(order)}
                              style={{ 
                                flex: 1,
                                padding: isMobile ? '8px' : '8px 12px', 
                                background: '#8b5cf6', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '8px', 
                                fontSize: isMobile ? '0.7rem' : '0.75rem', 
                                cursor: 'pointer', 
                                fontWeight: 600
                              }}
                            >
                              Dispatch
                            </button>
                          )}
                          
                          {isDelivery && (
                            <button
                              onClick={() => handleOpenOTPModal(order)}
                              style={{ 
                                flex: 1,
                                padding: isMobile ? '8px' : '8px 12px', 
                                background: '#10b981', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '8px', 
                                fontSize: isMobile ? '0.7rem' : '0.75rem', 
                                cursor: 'pointer', 
                                fontWeight: 600
                              }}
                            >
                              Deliver
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Order Details Modal - Mobile Optimized */}
      {showOrderModal && selectedOrder && (
        <>
          <div onClick={() => setShowOrderModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, backdropFilter: 'blur(4px)' }} />
          <div style={{ 
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            background: 'white', 
            borderRadius: '20px', 
            zIndex: 2001, 
            width: '90%', 
            maxWidth: '500px', 
            maxHeight: '85vh', 
            overflow: 'auto', 
            padding: isMobile ? '20px' : '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: isMobile ? '1rem' : '1.1rem', fontWeight: 700 }}>Order Details</h3>
              <button onClick={() => setShowOrderModal(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', border: 'none', cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Order ID</span>
                <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>#{selectedOrder.id}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Order Date</span>
                <span style={{ fontSize: '0.75rem' }}>{new Date(selectedOrder.orderDate).toLocaleDateString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Payment</span>
                <span style={{ fontSize: '0.75rem' }}>{selectedOrder.paymentMethod === 'Cash on Delivery' ? 'COD' : 'Online'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Total</span>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: '#dc2626' }}>₹{selectedOrder.totalAmount?.toLocaleString()}</span>
              </div>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
              <h4 style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '12px' }}>Customer</h4>
              <div style={{ marginBottom: '6px', fontSize: '0.8rem' }}><strong>Name:</strong> {selectedOrder.customerName}</div>
              <div style={{ marginBottom: '6px', fontSize: '0.8rem' }}><strong>Phone:</strong> {selectedOrder.customerPhone}</div>
              <div><strong>Address:</strong> {selectedOrder.deliveryAddress?.line1}</div>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
              <h4 style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '12px' }}>Items</h4>
              {selectedOrder.items?.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: idx !== selectedOrder.items.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{item.name}</div>
                    <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Qty: {item.quantity}</div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>₹{(item.price * item.quantity).toLocaleString()}</div>
                </div>
              ))}
            </div>

            {selectedOrder.deliveryTime && (
              <div style={{ background: '#fefaf8', borderRadius: '12px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Clock size={14} color="#dc2626" />
                  <span><strong>Delivery:</strong> {selectedOrder.deliveryTime}</span>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button onClick={() => handleCall(selectedOrder.customerPhone)} style={{ flex: 1, padding: '10px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '0.75rem' }}>
                <Phone size={14} /> Call
              </button>
              {selectedOrder.orderStatus === 'confirmed' && (
                <button onClick={() => handleProcessOrder(selectedOrder)} style={{ flex: 1, padding: '10px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}>
                  Process
                </button>
              )}
              {selectedOrder.orderStatus === 'processing' && (
                <button onClick={() => handleOpenTimeModal(selectedOrder)} style={{ flex: 1, padding: '10px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}>
                  Dispatch
                </button>
              )}
              {selectedOrder.orderStatus === 'out' && (
                <button onClick={() => handleOpenOTPModal(selectedOrder)} style={{ flex: 1, padding: '10px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}>
                  Deliver
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Time Selector Modal - Mobile Optimized */}
      {showTimeModal && selectedOrder && (
        <>
          <div onClick={() => setShowTimeModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '20px', zIndex: 1101, width: '90%', maxWidth: '400px', padding: isMobile ? '20px' : '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Set Delivery Time</h3>
              <button onClick={() => setShowTimeModal(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', border: 'none', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '8px' }}>Select Time</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select value={selectedHour} onChange={(e) => setSelectedHour(e.target.value)} style={{ padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', background: 'white', flex: 1, textAlign: 'center' }}>
                  {hours.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <span style={{ fontSize: '1.1rem' }}>:</span>
                <select value={selectedMinute} onChange={(e) => setSelectedMinute(e.target.value)} style={{ padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', background: 'white', flex: 1, textAlign: 'center' }}>
                  {minutes.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)} style={{ padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', background: 'white', flex: 1, textAlign: 'center' }}>
                  {periods.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowTimeModal(false)} style={{ flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDispatch} style={{ flex: 1, padding: '10px', background: '#dc2626', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Confirm</button>
            </div>
          </div>
        </>
      )}

      {/* OTP Verification Modal - Mobile Optimized */}
      {showOTPModal && selectedOrder && (
        <>
          <div onClick={() => setShowOTPModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '20px', zIndex: 1101, width: '90%', maxWidth: '350px', padding: isMobile ? '20px' : '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700 }}>Verify OTP</h3>
              <button onClick={() => setShowOTPModal(false)} style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', border: 'none', cursor: 'pointer' }}>✕</button>
            </div>
            <input 
              type="text" 
              maxLength={6} 
              value={deliveryOTP} 
              onChange={(e) => setDeliveryOTP(e.target.value.replace(/\D/g, ''))} 
              placeholder="6-digit OTP" 
              style={{ 
                width: '100%', 
                padding: '12px', 
                textAlign: 'center', 
                fontSize: '1.2rem', 
                letterSpacing: '4px', 
                fontFamily: 'monospace', 
                border: '1px solid #e2e8f0', 
                borderRadius: '10px', 
                marginBottom: '16px'
              }}
              autoFocus 
            />

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowOTPModal(false)} style={{ flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleVerifyDelivery} disabled={deliveryOTP.length !== 6} style={{ flex: 1, padding: '10px', background: deliveryOTP.length === 6 ? '#10b981' : '#94a3b8', color: 'white', borderRadius: '8px', cursor: deliveryOTP.length === 6 ? 'pointer' : 'not-allowed', fontWeight: 600 }}>Verify</button>
            </div>
          </div>
        </>
      )}

      <style>{`
        .spinner { animation: spin 0.6s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        
        @media (max-width: 768px) {
          input, select, button {
            font-size: 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default StaffPanel;